import { Component, Injector, OnInit, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    AnnualReportMetaInformationDTO,
    AnnualReportProvider,
    AnnualReportSharesProvider,
    CreateShareDto,
} from '@modeso/aumico-lib-annualreport-fe';
import {
    EMPTY_ARRAY_LENGTH,
    IInvitationDialogData,
    InviteUsersFailureDialogActions,
    ModalComponent,
    ModalData,
    ModalTitleIcons,
    ShareReportDialogActions,
    ShareType,
} from '@modeso/aumico-lib-core-fe';
import { UntilDestroy } from '@ngneat/until-destroy';
import { filter, first } from 'rxjs';
import { Constants } from '../../../../../aumico-lib-core-fe/src/lib/constants/constants';
import { ConfirmShareReportDialogComponent } from '../../components/confirm-share-report-dialog/confirm-share-report-dialog.component';
import { InviteDialogComponent } from '../../components/invite-dialog/invite-dialog.component';
import { ConfirmShareModalData } from '../../interfaces/confirm-share-modal-data.interface';
import { localizationKeys } from '../../utils/localizationKeys';
import { BasePageComponent } from '../base.page';

@UntilDestroy()
@Component({
    selector: 'invitation-add',
    template: `<ng-content></ng-content>`,
})
export class InvitationBasePage extends BasePageComponent implements OnInit {
    protected invitationDialogRef: MatDialogRef<InviteDialogComponent>;
    protected currentOrganizationUserEmails: string[] = [];
    protected providerOfAnnualReports: AnnualReportProvider;
    protected shareAnnualReportProvider = inject(AnnualReportSharesProvider);
    protected metaInformationId: string;
    protected metaInformation: AnnualReportMetaInformationDTO;
    private dialogData: IInvitationDialogData;
    constructor(injector: Injector, dialog: MatDialog) {
        super(injector, dialog);
        this.providerOfAnnualReports = injector.get(AnnualReportProvider);
    }
    protected openInvitationDialog(invitationDialogData: IInvitationDialogData): void {
        this.dialogData = invitationDialogData;
        this.invitationDialogRef = this.dialog.open(InviteDialogComponent, {
            width: '668px',
            data: invitationDialogData,
            panelClass: 'custom-modalbox',
        });
    }
    protected onHasFailedMails(failedMails: string[]): void {
        let emailsExistingInOtherOrganizationsText = '';
        let emailsExistingInSameOrganizationText = '';
        failedMails.forEach((email) => {
            if (this.currentOrganizationUserEmails.includes(email)) {
                emailsExistingInSameOrganizationText = emailsExistingInSameOrganizationText.concat(email).concat('\n');
            } else {
                emailsExistingInOtherOrganizationsText = emailsExistingInOtherOrganizationsText
                    .concat(email)
                    .concat('\n');
            }
        });

        const contentTexts = [];
        if (emailsExistingInOtherOrganizationsText !== '') {
            contentTexts.push('aumico_reports_invite_users_email_already_exist_error_message');
            contentTexts.push(emailsExistingInOtherOrganizationsText);
        }

        if (emailsExistingInSameOrganizationText !== '') {
            contentTexts.push('aumico_reports_invite_users_cannot_invite_same_organization_users');
            contentTexts.push(emailsExistingInSameOrganizationText);
        }

        this.dialog.open<ModalComponent, ModalData, InviteUsersFailureDialogActions>(ModalComponent, {
            disableClose: false,
            panelClass: 'custom-modalbox',
            data: {
                showCloseButton: true,
                titleIcon: ModalTitleIcons.WARNING,
                titleLocalizationKey: Constants.ERROR_DIALOG.SOMETHING_WENT_WRONG,
                footerButtons: [
                    {
                        localizationKey: 'aumico_reports_invite_users_failure_okay_button_label',
                        action: InviteUsersFailureDialogActions.OKAY,
                        disabled: false,
                        buttonColor: 'primary',
                    },
                ],
                content: {
                    text: contentTexts,
                },
            },
        });
    }

    protected hasFailedEmails(failedMails: string[]): boolean {
        return failedMails.length !== EMPTY_ARRAY_LENGTH;
    }

    protected shareHandler(emails: string[], emailMessage: string) {
        const dialogRef = this.openShareConfirmationDialog(emails);
        dialogRef
            ?.afterClosed()
            .pipe(
                first(),
                filter((result) => result != null),
            )
            .subscribe((result) => {
                if (result === ShareReportDialogActions.SHARE) {
                    const shareAnnualReportDto: CreateShareDto = {
                        message: emailMessage,
                        emails,
                        type: ShareType.ANNUAL_REPORT_SHARE,
                        entityId: this.metaInformationId || this.activatedRoute.snapshot.params['id'],
                    };
                    this.shareAnnualReportProvider.dispatchShareAnnualReport(shareAnnualReportDto);
                } else {
                    this.openInvitationDialog(this.dialogData);
                    this.invitationDialogRef.componentInstance.emailChips = emails;
                    this.invitationDialogRef.componentInstance.emailControl.setValue(emailMessage);
                }
            });
    }
    private openShareConfirmationDialog(emails: string[]) {
        return this.dialog.open<ModalComponent, ConfirmShareModalData, ShareReportDialogActions>(
            ConfirmShareReportDialogComponent,
            {
                disableClose: false,
                panelClass: 'custom-modalbox',
                data: {
                    showCloseButton: true,
                    titleIcon: ModalTitleIcons.DEFAULT,
                    emails,
                    footerButtons: [
                        {
                            localizationKey:
                                localizationKeys.aumico_reports_share_annual_report_confirmation_dialog_save_button,
                            action: ShareReportDialogActions.SHARE,
                            buttonColor: 'primary',
                        },
                        {
                            localizationKey:
                                localizationKeys.aumico_reports_share_annual_report_confirmation_go_back_cancel_button,
                            action: ShareReportDialogActions.CANCEL,
                            buttonColor: 'basic',
                        },
                    ],
                    content: {
                        text: [localizationKeys.aumico_reports_share_annual_report_confirmation_dialog_message],
                        icon: 'document-information',
                    },
                },
            },
        );
    }
}
